import React, { useState } from 'react';
import classes from './styles.module.scss';
import { PlusIcon, XIcon } from '../../../assets/icons';
import { useTranslation } from 'react-i18next';
import { deleteImage, uploadImage, uploadInfrastructureProjectImage, uploadStreetImage } from '../../../requests/supervisor.request';
import { useKato } from '../../../contexts/kato.context';
import { getImageById } from '../../../requests/public.request';
import { useShared } from '../../../contexts/shared.context';
import Modal from '../../../components/modal/modal.component';
import { useParams } from 'react-router-dom';
import { IFile, IInfrastructureProjectObject, InfrastructureProjectType } from '../../../types/project.types';
import { formatDate } from '../../home/components/left-menu/info.component';

interface IProps {
  kato: number;
  objectId?: number;
  imageIds?: number[];
  type?: string;
  imageData?: {
    IMAGE_AFTER: { id: number; date: string }[];
    IMAGE_CURRENT: { id: number; date: string }[];
  };
  // afterUpload?: (file: IFile) => void;
  afterUploadStreet?: (file: IFile) => void;
  setFormData?: React.Dispatch<React.SetStateAction<IInfrastructureProjectObject>>;
}

const baseClass = 'image-grid';

const ImageGrid = ({ kato, objectId, imageIds, type, imageData, afterUploadStreet, setFormData }: IProps) => {
  const { t } = useTranslation();
  const { updatedGeneralInfo } = useKato();
  const { selectedInfoYear } = useShared();
  const { tab, link } = useParams<{ tab: string, link: string }>();

  const [isOpen, setIsOpen] = useState(false);
  const [uploadDate, setUploadDate] = useState<Date | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<'IMAGE_CURRENT' | 'IMAGE_AFTER' | ''>('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetModal = () => {
    setUploadDate(null);
    setUploadFile(null);
    setUploadType('');
    setIsOpen(false);
    setIsEditing(false);
  };

  const afterUpload = (file: IFile) => {
    if (imageData?.[file.fileType]?.map(item => item.id).includes(file.id)) {
      setFormData?.(prev => ({
        ...prev,
        images: {
          ...prev.images,
          [file.fileType]: prev.images[file.fileType].filter(item => item.id !== file.id)
        }
      }));
    } else {
      setFormData?.(prev => ({
        ...prev,
        images: {
          ...prev.images,
          [file.fileType]: [...(prev.images?.[file.fileType] || []), file]
        }
      }));
    }
  }

  const handleDeleteImage = (image: IFile) => {
    deleteImage(image.id).then(() => {
      type === 'general-info'
        ? updatedGeneralInfo() :
        type === 'STREET' ? afterUploadStreet?.(image) : afterUpload?.(image)
    });
  };

  const handleUploadGeneralImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loading) return;
    if (e.target.files) {
      const file = e.target.files[0];
      if (file) {
        setLoading(true);
        uploadImage(kato, file, selectedInfoYear).then(() => {
          updatedGeneralInfo();
          e.target.value = '';
        }).finally(() => {
          setLoading(false);
        });
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleEditImage = (imageId: number) => {
    const image = imageData?.IMAGE_CURRENT?.find((img) => img.id === imageId) ||
      imageData?.IMAGE_AFTER?.find((img) => img.id === imageId);
    if (image) {
      setIsEditing(true);
      setUploadDate(new Date(image.date));
      setUploadType(imageData?.IMAGE_CURRENT?.some((img) => img.id === imageId) ? 'IMAGE_CURRENT' : 'IMAGE_AFTER');
      setUploadFile(new File([], '')); // Placeholder, as we cannot edit the file directly
      setIsOpen(true);
    }
  };

  const handleModalUpload = () => {
    if (!uploadDate || !uploadFile || !uploadType || !objectId || !tab) return;
    if (type === 'STREET') {
      uploadStreetImage(
        kato,
        objectId,
        uploadFile,
        tab as 'VILLAGE_ROAD' | 'LIGHTING',
        uploadType as 'IMAGE_CURRENT' | 'IMAGE_AFTER',
        new Date(uploadDate)
      ).then((res) => {
        resetModal();
        afterUploadStreet?.({
          id: +res.data,
          fileType: uploadType,
          date: uploadDate.toISOString(),
        } as IFile);
      });
    } else {
      const type = ['SNP_INVESTMENT', 'SNP_IMPROVEMENT'].includes(link as string) ? link : tab as InfrastructureProjectType;
      uploadInfrastructureProjectImage(
        kato,
        objectId,
        type as InfrastructureProjectType,
        uploadType,
        new Date(uploadDate),
        uploadFile
      ).then((res) => {
        updatedGeneralInfo();
        resetModal();
        afterUpload?.({
          id: +res.data,
          fileType: uploadType,
          date: uploadDate.toISOString(),
        } as IFile);
      });
    }
  };

  return (
    <div className={`${classes[baseClass]} ${type !== 'general-info' ? classes[`${baseClass}--grid`] : ''}`}>
      {type === 'general-info' ? (
        <>
          <label className={classes[`${baseClass}__add`]} htmlFor="upload-general-image">
            <PlusIcon />
            <span>{t(`actions.add-photo`)}</span>
            <input type="file" id="upload-general-image" onChange={handleUploadGeneralImage} />
          </label>

          {!!imageIds?.length &&
            imageIds.map((imageId) => (
              <div key={imageId} className={classes[`${baseClass}__image`]}>
                <img src={getImageById(imageId)} alt="Фотография" />
                <div className={classes[`${baseClass}__image-overlay`]}>
                  <span onClick={() => handleDeleteImage({ id: imageId } as IFile)}>
                    <XIcon /> {t(`actions.delete`)}
                  </span>
                </div>
              </div>
            ))}
        </>
      ) : (
        <>
          <button className={classes[`${baseClass}__add`]} onClick={() => setIsOpen(true)}>
            <PlusIcon />
            <span>{t(`actions.add-photo`)}</span>
          </button>

          {['IMAGE_AFTER', 'IMAGE_CURRENT'].map((key) => (
            imageData?.[key as 'IMAGE_AFTER' | 'IMAGE_CURRENT'] && !!imageData?.[key as 'IMAGE_AFTER' | 'IMAGE_CURRENT'].length && <div key={key}>
              <div className={classes[`${baseClass}__section-title`]}>
                {t(`projects.${key}`)}
              </div>
              <div className={classes[`${baseClass}__images`]}>
                {imageData?.[key as 'IMAGE_AFTER' | 'IMAGE_CURRENT']?.map((img) => (
                  <div key={img.id} className={classes[`${baseClass}__image`]}>
                    <img src={getImageById(img.id)} alt="Фотография" />
                    <span className={classes[`${baseClass}__image-date`]}>
                      {formatDate(img.date)}
                    </span>
                    <div className={`${classes[`${baseClass}__image-overlay`]} `}>
                      <span onClick={() => handleDeleteImage({ ...img, fileType: key } as IFile)}>
                        <XIcon /> {t(`actions.delete`)}
                      </span>
                      {/* <span onClick={() => handleEditImage(img.id)}>
                        <PenIcon /> {t(`actions.edit`)}
                      </span> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      <Modal show={isOpen} handleClose={resetModal}>
        <div className={classes[`${baseClass}__modal`]}>
          {!isEditing && <div className={classes[`${baseClass}__modal-field`]}>
            <span>{t(`image-grid.file`)}:</span>

            {uploadFile ? <>
              <span>{uploadFile.name}</span>
            </>
              : <>
                <label className={classes[`${baseClass}__modal-file-name`]}>
                  {t(`image-grid.choose-file`)}
                  <input type="file" onChange={handleFileChange} />
                </label>
              </>}
          </div>}

          <div className={classes[`${baseClass}__modal-field`]}>
            <label>{t(`image-grid.date`)}</label>
            <input
              type="date"
              value={uploadDate ? uploadDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setUploadDate(e.target.value ? new Date(e.target.value) : null)}
            />
          </div>

          <div className={classes[`${baseClass}__modal-field`]}>
            <label>{t(`image-grid.type`)}</label>
            <select
              value={uploadType}
              onChange={(e) =>
                setUploadType(e.target.value as 'IMAGE_CURRENT' | 'IMAGE_AFTER')
              }
            >
              <option value="" disabled hidden>
              </option>
              <option value="IMAGE_CURRENT">{t(`projects.IMAGE_CURRENT`)}</option>
              <option value="IMAGE_AFTER">{t(`projects.IMAGE_AFTER`)}</option>
            </select>
          </div>

          <div className={classes[`${baseClass}__modal-actions`]}>
            <button onClick={handleModalUpload}>{t(`actions.save`)}</button>
            <button onClick={resetModal}>{t(`actions.cancel`)}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ImageGrid;
